// One training session (Session AFL): its facts, its attendees, the lifts it covered, the office
// and customer notes, and the actions that issue and download the certificate.
//
// WHAT THE PAGE WILL AND WILL NOT LET YOU DO mirrors the server exactly, so the office never meets
// a refusal it could not see coming. While a session is SCHEDULED everything is editable, through
// Edit session (the date, the type, the duration, who delivered it and the lift set) plus the
// attendee and notes cards. Marking it completed issues a numbered certificate, and from then on
// only the notes and the attendees stay editable: the date, the type and the lift set are what the
// certificate and the two-year renewal date are worked out from, so changing them would rewrite a
// document the customer already holds, and Edit session is not offered at all.
//
// Deleting is one plain delete (Session AFM, Director decision): a training session is an
// internal operational record, so a single confirmation removes it, certificate or not.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { AssignEngineerField } from "./AssignEngineerField";
import { EditTrainingSessionModal } from "./EditTrainingSessionModal";
import { TrainingReferenceIndicator } from "./TrainingReferenceIndicator";
import { TrainingSessionReferenceSend } from "./TrainingSessionReferenceSend";
import { TRAINING_STATUS_LABELS, trainingRenewalState, trainingTypeLabel } from "../../lib/trainingTypes";
import type { TrainingSessionDetailDto, TrainingSessionUpdateInput } from "../../lib/trainingTypes";
import { Button, buttonClasses } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate } from "../../lib/format";

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const cardCls = "rounded-ele border border-ele-purple-accent bg-ele-surface p-5";
const cardHeadingCls = "mb-3 font-heading text-lg text-ele-blue";
const ghostBtn =
  "rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50 max-sm:min-h-11";

const RENEWAL_COPY: Record<"ok" | "due_soon" | "overdue", string> = {
  ok: "Renewal due",
  due_soon: "Renewal due soon",
  overdue: "Renewal overdue",
};

export function TrainingDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState<TrainingSessionDetailDto | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoadError(false);
    api.training
      .get(id)
      .then((s) => {
        setSession(s);
        setLoaded(true);
      })
      .catch(() => {
        setLoaded(true);
        setLoadError(true);
      });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const renewal = useMemo(() => trainingRenewalState(session?.renewalDueDate ?? null), [session?.renewalDueDate]);
  const scheduled = session?.status === "SCHEDULED";
  const completed = session?.status === "COMPLETED";

  // The one save path for every field on this page. It reports whether the change landed, so a
  // caller with a form open (the edit modal) can stay open on a refusal with the server's own
  // message showing, rather than closing over an edit that was never stored.
  async function patch(body: TrainingSessionUpdateInput, successMessage: string): Promise<boolean> {
    setActionError(null);
    // The previous success too, not just the previous error. Otherwise a second save that is refused
    // leaves the green banner from the first one sitting beside the red one, which reads as though
    // the change both landed and did not. The inline picker made that routine: it is the one control
    // here an office user naturally operates twice in a row.
    setNotice(null);
    try {
      const updated = await api.training.update(id, body);
      setSession(updated);
      setNotice(successMessage);
      return true;
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message || e.code : "Could not save the change. Please try again.");
      return false;
    }
  }

  // Saved on change, because a dropdown with its own Save button beside a page that has none reads
  // as unsaved. It goes through the same patch() as everything else, so a refusal shows the server's
  // message and the select falls back to the stored value rather than displaying a change that was
  // never stored. Assigning is reversible and touches nobody outside ELE, so no two-step confirm:
  // what it costs is said in the field's own helper text instead.
  const [assigning, setAssigning] = useState(false);
  async function assignEngineer(userId: string) {
    if ((session?.assignedEngineerId ?? "") === userId) return;
    setAssigning(true);
    await patch(
      { assignedEngineerId: userId || null },
      userId
        ? "Engineer assigned. It now shows on their phone."
        : "Assignment cleared. It now shows on no engineer's phone until you name someone.",
    );
    setAssigning(false);
  }

  async function complete() {
    setCompleting(true);
    setActionError(null);
    try {
      const result = await api.training.complete(id);
      setCompleteOpen(false);
      setNotice(`Training completed. Certificate ${result.certificateRef} issued.`);
      load();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message || e.code : "Could not complete this session. Please try again.");
      setCompleteOpen(false);
    } finally {
      setCompleting(false);
    }
  }

  async function runDelete() {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.training.delete(id);
      navigate("/office/training");
    } catch (e) {
      setDeleteError(e instanceof ApiError ? e.message || e.code : "Could not delete this training session. Please try again.");
      setDeleteBusy(false);
    }
  }

  if (!loaded) return <Loader label="Loading training session..." shape="panel" className="py-6" />;
  if (loadError || !session) {
    return (
      <div>
        <Link to="/office/training" className="text-xs text-ele-body hover:text-ele-strong">
          &larr; Back to training
        </Link>
        <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          This training session could not be found.
        </p>
      </div>
    );
  }

  return (
    <div>
      <RecordHeader
        className="mb-4"
        title={trainingTypeLabel(session.trainingType, session.customLabel)}
        backTo="/office/training"
        backLabel="Back to training"
        status={<StatusPill word={TRAINING_STATUS_LABELS[session.status]} tone={statusEntry("training", session.status).tone} />}
        subtitle={
          <>
            <Link to={`/office/sites/${encodeURIComponent(session.siteId)}`} className="mr-3 text-ele-green hover:underline">
              {session.siteName}
            </Link>
            <span className="mr-3">{formatDate(session.sessionDate)}</span>
            {session.durationMinutes != null && <span>{session.durationMinutes} minutes</span>}
          </>
        }
        actions={
          <>
            {scheduled && (
              <Button size="sm" onClick={() => setCompleteOpen(true)} disabled={session.attendees.length === 0}>
                Mark as completed
              </Button>
            )}
            {/* Offered only while SCHEDULED, matching the server's freeze exactly. */}
            {scheduled && (
              <button type="button" onClick={() => setEditOpen(true)} className={ghostBtn}>
                Edit session
              </button>
            )}
            {completed && (
              <a
                href={api.training.certificateHref(session.id)}
                className="max-sm:min-h-11 max-sm:inline-flex max-sm:items-center rounded-ele border border-ele-loler px-4 py-2 font-menu text-xs tracking-wide text-ele-loler transition hover:bg-ele-inset"
              >
                Download certificate
              </a>
            )}
            {scheduled && (
              <ConfirmButton
                onConfirm={() => void patch({ status: "CANCELLED" }, "Training session cancelled.")}
                label="Cancel session"
                confirmLabel="Press again to cancel"
                armedClassName="!bg-ele-danger-fill !text-white ring-2 ring-ele-danger"
                className={buttonClasses("destructive", "sm")}
              />
            )}
            {session.status === "CANCELLED" && (
              <button type="button" onClick={() => void patch({ status: "SCHEDULED" }, "Training session reopened.")} className={ghostBtn}>
                Reopen session
              </button>
            )}
          </>
        }
      />
      <div className="mb-4 min-w-0">
            {/* Who is delivering it. BOTH of these have been settable since Session AGG but were
                displayed nowhere in the office, so a session's assignment could only be discovered by
                opening the edit modal. deliveredBy is the free-text override for when it is not the
                assigned engineer, so the two are shown together rather than either standing alone. */}
            <p className="mt-2 text-sm text-ele-body">
              {session.assignedEngineerName ? (
                <>
                  Assigned to <span className="text-ele-strong">{session.assignedEngineerName}</span>
                </>
              ) : (
                "Not assigned. Shows on no engineer's phone until you name someone."
              )}
              {session.deliveredBy && <>. Delivered by {session.deliveredBy}</>}
            </p>
            {/* Assigning from the page itself (Session AGN). The picker has been on the edit modal
                since AGG, which is three clicks and a save to change one field, and it was buried
                far enough that it read as missing. Same component, same options, same PATCH, so
                there is one picker in the office and the two cannot drift.

                SCHEDULED only, matching Edit session and the paragraph at the top of this file. The
                server would allow it later (assignedEngineerId is in its always-editable set), but
                the PWA only ever lists SCHEDULED sessions, so assigning a finished one would change
                nothing while looking like it had. */}
            {scheduled && (
              <div className="mt-3 max-w-xs">
                <AssignEngineerField
                  id="training-detail-assigned-engineer"
                  value={session.assignedEngineerId ?? ""}
                  onChange={(userId) => void assignEngineer(userId)}
                  labelClassName={labelCls}
                  selectClassName={`${inputCls} disabled:opacity-60`}
                  // Held while the edit modal is open as well as while saving. ModalShell has no
                  // focus trap, so this field stays keyboard-reachable behind the dialog, and the
                  // modal re-seeds every field whenever the session object changes. One change here
                  // would therefore reset an unsaved date correction in the dialog in front of it,
                  // and the date is what the certificate and the renewal are worked out from. While
                  // the modal is open it owns this field.
                  disabled={assigning || editOpen}
                />
                {assigning && (
                  <p className="mt-1 text-xs text-ele-body" role="status">
                    Saving...
                  </p>
                )}
              </div>
            )}
            {session.certificateRef && (
              <p className="mt-2 text-sm text-ele-body">
                Certificate <span className="ele-ref">{session.certificateRef}</span>
                {session.certificateIssuedAt && <> issued {formatDate(session.certificateIssuedAt)}</>}
              </p>
            )}
            {session.renewalDueDate && renewal && (
              <p
                className={`mt-1 text-sm ${renewal === "ok" ? "text-ele-body" : "text-[var(--ele-amber-text)]"}`}
                role={renewal === "ok" ? undefined : "status"}
              >
                {RENEWAL_COPY[renewal]}: {formatDate(session.renewalDueDate)}
              </p>
            )}
      </div>

      {/* Explains the disabled "Mark as completed" button ABOVE with a real paragraph, not a title
          attribute: a disabled control's title never paints in Chromium/WebKit (Session AIE), so the
          reason has to be visible on the page itself, not hidden behind a hover state nobody can
          trigger on a disabled button. */}
      {scheduled && session.attendees.length === 0 && (
        <p className="mb-4 text-sm text-ele-body">
          Add at least one attendee below before you can complete this session.
        </p>
      )}

      {notice && (
        <p role="status" className="mb-4 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}
      {actionError && (
        <p role="alert" className="mb-4 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
          {actionError}
        </p>
      )}
      {completed && (
        <p className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-body">
          This session is completed and its certificate issued. The date, training type and lifts are fixed; the notes can still be updated.
        </p>
      )}

      <TrainingReferenceIndicator trainingType={session.trainingType} className="mb-4" />

      <div className="space-y-5">
        {/* Session AFP (Stream B1): once completed, offer to send the customer-facing reference
            document to the customer. Shows nothing when none is on file; never blocks completion. */}
        {completed && <TrainingSessionReferenceSend trainingType={session.trainingType} siteId={session.siteId} />}
        <AttendeesCard session={session} onChanged={setSession} onError={setActionError} escalateRemove={completed} />
        <LiftsCard session={session} editable={scheduled} />
        <NotesCard session={session} onSave={patch} />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button type="button" onClick={() => setDeleteOpen(true)} className="text-sm text-ele-danger underline hover:brightness-110">
          Delete this session
        </button>
      </div>

      <EditTrainingSessionModal
        open={editOpen && scheduled}
        session={session}
        onClose={() => setEditOpen(false)}
        onSave={(body) => patch(body, "Training session updated.")}
      />

      {completeOpen && (
        <ModalShell ariaLabel="Mark training as completed" onClose={() => !completing && setCompleteOpen(false)} closeOnBackdrop width="max-w-md">
          <h2 className="mb-3 font-heading text-xl text-ele-strong">Mark as completed?</h2>
          <p className="text-sm text-ele-body">
            Issue the training certificate and mark this session complete? The certificate reference is assigned when you confirm.
          </p>
          <p className="mt-2 text-sm text-ele-body">
            The renewal date is set to two years from the training date, {formatDate(session.sessionDate)}. After this, the date, training type
            and lifts can no longer be changed.
          </p>
          <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
            <button type="button" onClick={() => setCompleteOpen(false)} disabled={completing} className={`${ghostBtn} ${MODAL_ACTION_BTN}`}>
              Cancel
            </button>
            <Button size="sm" onClick={() => void complete()} disabled={completing} className={MODAL_ACTION_BTN}>
              {completing ? "Issuing..." : "Issue certificate"}
            </Button>
          </div>
        </ModalShell>
      )}

      {/* One confirmation, then gone (Session AFM, Director decision): singleStep opts this
          delete out of the central two-step arming, because the modal itself is the one
          confirmation the Director asked for on this internal operational record. */}
      <ConfirmDeleteModal
        open={deleteOpen}
        title="Delete this training session?"
        message="This removes the session, its attendees and its lift links."
        warning={session.certificateRef ? `Certificate ${session.certificateRef} was issued for this session.` : null}
        confirmLabel="Delete"
        variant="destructive"
        singleStep
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => void runDelete()}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleteOpen(false);
          setDeleteError(null);
        }}
      />
    </div>
  );
}

// ---- Attendees --------------------------------------------------------------

function AttendeesCard({
  session,
  onChanged,
  onError,
  escalateRemove,
}: {
  session: TrainingSessionDetailDto;
  onChanged: (s: TrainingSessionDetailDto) => void;
  onError: (m: string | null) => void;
  /** A completed session's attendee names are on an issued certificate, so removing one escalates. */
  escalateRemove: boolean;
}) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  async function add() {
    if (!name.trim()) return;
    setBusy(true);
    onError(null);
    try {
      await api.training.addAttendee(session.id, { name: name.trim(), role: role.trim() || null, email: email.trim() || null });
      onChanged(await api.training.get(session.id));
      setName("");
      setRole("");
      setEmail("");
    } catch (e) {
      onError(e instanceof ApiError ? e.message || e.code : "Could not add the attendee. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(attendeeId: string) {
    setRemovingId(attendeeId);
    onError(null);
    try {
      await api.training.removeAttendee(session.id, attendeeId);
      onChanged(await api.training.get(session.id));
    } catch (e) {
      onError(e instanceof ApiError ? e.message || e.code : "Could not remove the attendee. Please try again.");
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className={cardCls}>
      <h2 className={cardHeadingCls}>Attendees</h2>
      {session.attendees.length === 0 ? (
        <p className="text-sm text-ele-body">No attendees recorded yet.</p>
      ) : (
        <ul className="mb-4 divide-y divide-ele-purple-accent">
          {session.attendees.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span className="min-w-0">
                <span className="text-sm text-ele-strong">{a.name}</span>
                {a.role && <span className="ml-2 text-sm text-ele-body">{a.role}</span>}
                {a.email && <span className="ml-2 text-xs text-ele-body">{a.email}</span>}
                {a.hasSignature && <StatusPill className="ml-2" word="Signed" tone="green" />}
              </span>
              <ConfirmButton
                onConfirm={() => void remove(a.id)}
                label="Remove"
                confirmLabel={escalateRemove ? "Press again: on the certificate" : "Confirm?"}
                busy={removingId === a.id}
                busyLabel="Removing..."
                title={escalateRemove ? `${a.name} appears on the issued certificate` : `Remove ${a.name}`}
                className="font-menu text-xs tracking-wide text-ele-body"
              />
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className={labelCls} htmlFor="add-attendee-name">
            Name
          </label>
          <input id="add-attendee-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="add-attendee-role">
            Role (optional)
          </label>
          <input id="add-attendee-role" value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="add-attendee-email">
            Email (optional)
          </label>
          <input id="add-attendee-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        </div>
      </div>
      <button type="button" onClick={() => void add()} disabled={busy || !name.trim()} className={`mt-3 ${ghostBtn} max-sm:w-full`}>
        {busy ? "Adding..." : "Add attendee"}
      </button>
    </div>
  );
}

// ---- Lifts ------------------------------------------------------------------

function LiftsCard({ session, editable }: { session: TrainingSessionDetailDto; editable: boolean }) {
  return (
    <div className={cardCls}>
      <h2 className={cardHeadingCls}>Lifts covered</h2>
      {session.lifts.length === 0 ? (
        <p className="text-sm text-ele-body">
          No specific lifts: this was general training for the building.
          {editable && " Use Edit session to add lifts while this session is scheduled."}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {session.lifts.map((l) => (
            <li key={l.assetId}>
              <Link
                to={`/office/lifts/${encodeURIComponent(l.assetId)}`}
                className="inline-flex items-center gap-2 rounded-ele border border-ele-purple-accent px-3 py-1 transition hover:border-ele-green"
              >
                <span className="ele-ref">{l.eleNumber ?? "Not allocated"}</span>
                {l.liftType && <span className="text-xs text-ele-body">{l.liftType}</span>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---- Notes ------------------------------------------------------------------

function NotesCard({
  session,
  onSave,
}: {
  session: TrainingSessionDetailDto;
  onSave: (body: TrainingSessionUpdateInput, successMessage: string) => Promise<boolean>;
}) {
  const [notes, setNotes] = useState(session.notes ?? "");
  const [customerNotes, setCustomerNotes] = useState(session.customerNotes ?? "");
  const [busy, setBusy] = useState<"internal" | "customer" | null>(null);

  // Re-seed when the session reloads (a completion or an attendee change replaces the object).
  useEffect(() => {
    setNotes(session.notes ?? "");
    setCustomerNotes(session.customerNotes ?? "");
  }, [session.notes, session.customerNotes]);

  async function save(which: "internal" | "customer") {
    setBusy(which);
    try {
      if (which === "internal") await onSave({ notes: notes.trim() || null }, "Office notes saved.");
      else await onSave({ customerNotes: customerNotes.trim() || null }, "Customer notes saved.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className={cardCls}>
        <h2 className={cardHeadingCls}>Internal notes</h2>
        <label className={labelCls} htmlFor="training-internal-notes">
          Office notes: not shown to customer
        </label>
        <textarea id="training-internal-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
        <button type="button" onClick={() => void save("internal")} disabled={busy !== null} className={`mt-3 ${ghostBtn} max-sm:w-full`}>
          {busy === "internal" ? "Saving..." : "Save notes"}
        </button>
      </div>

      <div className={cardCls}>
        <h2 className={cardHeadingCls}>Customer notes</h2>
        <label className={labelCls} htmlFor="training-customer-notes-detail">
          Visible to customer in portal
        </label>
        <textarea
          id="training-customer-notes-detail"
          rows={4}
          value={customerNotes}
          onChange={(e) => setCustomerNotes(e.target.value)}
          className={inputCls}
        />
        <p className="mt-1 text-xs text-ele-body">These notes also print on the certificate.</p>
        <button type="button" onClick={() => void save("customer")} disabled={busy !== null} className={`mt-3 ${ghostBtn} max-sm:w-full`}>
          {busy === "customer" ? "Saving..." : "Save customer notes"}
        </button>
      </div>
    </>
  );
}
